import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { ChevronDown, Globe } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Button } from "@/modern/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/modern/components/ui/collapsible";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import {
  MarketingStackWorkflowStrip,
  marketingStackCardClassName,
  marketingStackContentInsetClassName,
  marketingStackPageClassName,
} from "@/react/components/marketingStack";
import { sitesApi } from "@/network/network.jsx";
import { useIntegrationErrorReporter } from "@/integrations";
import { cn } from "@/modern/utils";
import { applySiteFormData, mergeSiteApiIntoFormData, siteSchema, siteToFormData, type SiteFormData } from "@/react/schema/siteSchema";
import { useSite } from "../marketingPages/hooks/useSite";
import { useSiteFormAutoSave } from "../marketingPages/hooks/useSiteFormAutoSave";
import { confirmSlugRepublishIfNeeded } from "../marketingPages/hooks/useSlugRepublishConfirm";
import { SitePublishPanel } from "../marketingPages/components/SitePublishPanel";
import {
  AutoFillLink,
  AutoFillSeoConfirmDialog,
  FaviconUploader,
  Field,
  GO_LIVE_INPUT_CLASS,
  GO_LIVE_TEXTAREA_CLASS,
  GoLiveFormSection,
  GoLiveToggleRow,
  LivePreviewPanel,
  OgImageUploader,
  SiteEditorFooter,
  SiteEditorHeader,
  SiteEditorPreviewHeader
} from "../marketingPages/components/shared/siteEditorUi";
import { isSiteLive, resolveSitePreviewUrl, TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG, siteNeedsPersist } from "../marketingPages/utils";

const STATUS_CHIP_ID = "site-editor-status";

function SiteSetupProgress({ ensureError, isReadOnly, onRetry }: { ensureError: string | null; isReadOnly: boolean; onRetry: () => void }) {
  if (ensureError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-md space-y-4 rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center">
          <p className="text-sm text-destructive">{ensureError}</p>
          {!isReadOnly && (
            <Button type="button" variant="outline" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md space-y-5 rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="space-y-1 text-center">
          <h2 className="text-base font-semibold text-foreground">Setting up your site…</h2>
          <p className="text-sm text-muted-foreground">Creating a site record for this board. This usually takes a few seconds.</p>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div className="h-full w-full animate-pulse bg-primary/80" />
        </div>
      </div>
    </div>
  );
}

function TestDriveSiteNote() {
  return (
    <div className="shrink-0 border-b border-border/80 bg-muted/40 px-6 py-2.5">
      <p className="text-xs leading-snug text-muted-foreground sm:text-sm">
        {TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG}
      </p>
    </div>
  );
}

function TestDriveSiteEmptyState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md space-y-2 rounded-xl border border-border bg-card p-6 text-center shadow-sm">
        <h2 className="text-base font-semibold text-foreground">Sites unavailable</h2>
        <p className="text-sm text-muted-foreground">{TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG}</p>
      </div>
    </div>
  );
}

export function SiteEditorContent() {
  const { project, selectedBoard, userPreferences } = useSwivuBryntumContext();
  const { reportError } = useIntegrationErrorReporter();
  const boardSlug = (selectedBoard?.record as { slug?: string } | undefined)?.slug;

  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record: prefRecord }) => prefRecord.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey
  });

  const hasOpenAIKey = useMemo(() => {
    if (!userPreferences) return false;
    return userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";
  }, [userPreferences]);

  const {
    activeSite,
    isEnsuring,
    ensureError,
    syncSiteIfNeeded,
    isPublishing,
    isRepublishing,
    isDeploying,
    startPublish,
    startPublishWithDomain,
    includedPages,
    publishEnabled,
    publishTooltip,
    publishLiveEnabled,
    publishLiveTooltip,
    hasUnpublishedChanges,
    siteReady,
    isReadOnly,
    isTestDrive,
    boardId,
  } = useSite({ syncOnMount: true });

  const [isGeneratingSeo, setIsGeneratingSeo] = useState(false);
  const [isSeoConfirmOpen, setIsSeoConfirmOpen] = useState(false);
  const [isPublishStarting, setIsPublishStarting] = useState(false);
  const [isDeployStarting, setIsDeployStarting] = useState(false);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [, bumpDomainView] = useState(0);

  const isBusy = isPublishing || isRepublishing || isPublishStarting || isDeploying || isDeployStarting;

  const {
    control,
    reset,
    getValues,
    watch,
    trigger,
    formState: { errors, isDirty },
  } = useForm<SiteFormData>({
    resolver: zodResolver(siteSchema),
    defaultValues: activeSite ? siteToFormData(activeSite) : undefined,
    mode: "onChange",
  });

  const { isSaving, flushSave } = useSiteFormAutoSave({
    activeSite,
    project,
    siteReady,
    isReadOnly,
    isBusy,
    isEnsuring,
    isDirty,
    getValues,
    reset,
    trigger,
    watch,
  });

  useEffect(() => {
    if (activeSite) {
      reset(siteToFormData(activeSite));
    }
  }, [activeSite, reset]);

  const watchedMetaTitle = watch("metaTitle");
  const watchedMetaDescription = watch("metaDescription");
  const watchedOgTitle = watch("ogTitle");
  const watchedOgDescription = watch("ogDescription");
  const watchedOgImageUrl = watch("ogImageUrl");

  const handleSiteMediaUpdated = useCallback(() => {
    if (activeSite) {
      reset(siteToFormData(activeSite));
    }
  }, [activeSite, reset]);

  const handleDomainChanged = useCallback(() => {
    bumpDomainView((n) => n + 1);
  }, []);

  const persistBeforePublish = useCallback(async (): Promise<boolean> => {
    return flushSave();
  }, [flushSave]);

  const handleGenerateSeo = useCallback(async (): Promise<boolean> => {
    if (!boardSlug || isReadOnly || isEnsuring) return false;
    if (activeSite && siteNeedsPersist(activeSite)) {
      const synced = await syncSiteIfNeeded();
      if (!synced) {
        toast.error("Could not create site before generating SEO.");
        return false;
      }
    }
    setIsGeneratingSeo(true);
    try {
      const res = await sitesApi(boardSlug).generateSeo();
      const apiSite = res?.data ?? {};
      const merged = mergeSiteApiIntoFormData(getValues(), apiSite);
      if (activeSite) {
        applySiteFormData(activeSite, merged);
        await project?.sync();
      }
      reset(merged);
      toast.success("SEO copy drafted from your board — review and tweak before publishing.");
      return true;
    } catch (error) {
      reportError({
        error,
        provider: "openai",
        fallback: "Could not generate site SEO.",
      });
      return false;
    } finally {
      setIsGeneratingSeo(false);
    }
  }, [boardSlug, isReadOnly, isEnsuring, activeSite, syncSiteIfNeeded, getValues, project, reset, reportError]);

  const handleConfirmGenerateSeo = useCallback(async () => {
    const ok = await handleGenerateSeo();
    if (ok) setIsSeoConfirmOpen(false);
  }, [handleGenerateSeo]);

  const runPublishPreflight = useCallback(async (): Promise<boolean> => {
    const valid = await trigger();
    if (!valid) {
      toast.error("Fix form errors before publishing.");
      return false;
    }

    if (activeSite && siteNeedsPersist(activeSite)) {
      const synced = await syncSiteIfNeeded();
      if (!synced) {
        toast.error("Could not create site before publishing.");
        return false;
      }
    }

    const live = isSiteLive(activeSite);
    const slugDirty = getValues("slug") !== (activeSite?.slug ?? "");
    if (live && slugDirty) {
      const confirmed = await confirmSlugRepublishIfNeeded(true, true);
      if (!confirmed) return false;
    }

    return persistBeforePublish();
  }, [trigger, activeSite, syncSiteIfNeeded, getValues, persistBeforePublish]);

  const handlePreview = async (): Promise<boolean> => {
    if (isEnsuring || isBusy) return false;

    setIsPublishStarting(true);
    try {
      if (!(await runPublishPreflight())) return false;
      return await startPublish();
    } finally {
      setIsPublishStarting(false);
    }
  };

  const handlePublishLive = async (): Promise<boolean> => {
    if (isEnsuring || isBusy) return false;

    setIsDeployStarting(true);
    try {
      if (!(await runPublishPreflight())) return false;
      return await startPublishWithDomain();
    } finally {
      setIsDeployStarting(false);
    }
  };

  const publishedAtDate =
    activeSite?.publishedAt && !Number.isNaN(new Date(activeSite.publishedAt).getTime())
      ? new Date(activeSite.publishedAt)
      : null;
  const publishedAt = publishedAtDate ? format(publishedAtDate, "MMM d, yyyy · h:mm a") : null;
  const isLive = isSiteLive(activeSite);
  const previewUrl = resolveSitePreviewUrl(activeSite);
  const faviconPreviewUrl = activeSite?.faviconThumbnailUrl?.trim() || activeSite?.faviconUrl?.trim() || "";
  const previewProps = {
    title: watchedMetaTitle ?? "",
    url: previewUrl,
    description: watchedMetaDescription ?? "",
    socialTitle: watchedOgTitle || watchedMetaTitle || "",
    socialDescription: watchedOgDescription || watchedMetaDescription || "",
    imageUrl: activeSite?.ogImageThumbnailUrl?.trim() || watchedOgImageUrl?.trim() || activeSite?.ogImageUrl?.trim() || "",
    faviconUrl: faviconPreviewUrl,
  };

  const publishSubtitle = isSaving
    ? "Saving changes…"
    : isLive
      ? publishedAt
        ? `Last preview · ${publishedAt}`
        : "Preview published"
      : "Not previewed yet";
  const headerTitle = "Site";
  const HeaderIcon = Globe;

  const fieldDisabled = isBusy || isReadOnly;

  const headerActions =
    activeSite && siteReady ? (
      <SitePublishPanel
        site={activeSite}
        boardSlug={boardSlug}
        boardId={boardId}
        isReadOnly={isReadOnly}
        previewUrl={previewUrl}
        isLive={isLive}
        publishedAt={publishedAtDate}
        includedCount={includedPages.length}
        isBusy={isBusy}
        isPublishing={isPublishing}
        isRepublishing={isRepublishing}
        isDeploying={isDeploying}
        isPublishStarting={isPublishStarting}
        isDeployStarting={isDeployStarting}
        publishEnabled={publishEnabled}
        publishTooltip={publishTooltip}
        publishLiveEnabled={publishLiveEnabled}
        publishLiveTooltip={publishLiveTooltip}
        hasUnpublishedChanges={hasUnpublishedChanges}
        hasUnsavedEdits={isDirty || isSaving}
        onPreview={handlePreview}
        onPublishLive={handlePublishLive}
        onDomainChanged={handleDomainChanged}
      />
    ) : null;

  const showSetupState = isEnsuring || (ensureError && !siteReady);
  const showTestDriveEmpty = isTestDrive && !siteReady && !isEnsuring && !ensureError;

  return (
    <div className={cn(marketingStackPageClassName, "w-full flex-1 overflow-hidden")}>
      <MarketingStackWorkflowStrip activeView={VIEW_TYPES.site} />
      {isTestDrive && siteReady ? <TestDriveSiteNote /> : null}
      {showTestDriveEmpty ? (
        <div className={marketingStackContentInsetClassName}>
          <TestDriveSiteEmptyState />
        </div>
      ) : showSetupState ? (
        <div className={marketingStackContentInsetClassName}>
          <SiteSetupProgress ensureError={ensureError} isReadOnly={isReadOnly} onRetry={() => void syncSiteIfNeeded()} />
        </div>
      ) : (
        <div className={marketingStackContentInsetClassName}>
          <div className={marketingStackCardClassName()}>
            <SiteEditorHeader title={headerTitle} icon={HeaderIcon} isLive={isLive} isReadOnly={isReadOnly} trailing={headerActions} />

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex min-h-0 flex-1 overflow-hidden lg:flex-row">
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-6 py-5">
                <div className="mb-6 flex shrink-0 justify-end">
                  <AutoFillLink
                    onClick={() => setIsSeoConfirmOpen(true)}
                    disabled={isReadOnly || isEnsuring || isBusy || Boolean(ensureError) || !hasOpenAIKey}
                    loading={isGeneratingSeo}
                    tooltip={
                      isTestDrive
                        ? TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG
                        : !hasOpenAIKey
                          ? "Add an OpenAI API key in Settings to auto-fill SEO from your board."
                          : undefined
                    }
                  />
                  <AutoFillSeoConfirmDialog
                    open={isSeoConfirmOpen}
                    onOpenChange={setIsSeoConfirmOpen}
                    onConfirm={handleConfirmGenerateSeo}
                    loading={isGeneratingSeo}
                  />
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                  <div className="space-y-6">
                    <GoLiveFormSection
                      title="Site address"
                      subtitle="The public URL path where visitors find your site."
                      helpText="The slug becomes part of your site URL. Use lowercase letters, numbers, and hyphens (e.g. smith-roofing-austin). Changing it after publish updates the live address and may require republishing."
                      first
                    >
                      <Field label="Slug" required error={errors.slug?.message} variant="goLive">
                        <Controller
                          name="slug"
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              variant="outline"
                              inputSize="sm"
                              disabled={fieldDisabled}
                              className={cn(GO_LIVE_INPUT_CLASS, "font-mono")}
                              autoComplete="off"
                            />
                          )}
                        />
                      </Field>
                    </GoLiveFormSection>

                    <GoLiveFormSection
                      title="Search appearance"
                      subtitle="How your homepage shows up in Google and other search results."
                      helpText="Page title: aim for about 60 characters — it's the blue headline in search results. Description: a short summary, ideally 150–160 characters. Allow indexing: when off, search engines are asked not to list this site."
                    >
                      <Field label="Page title" variant="goLive">
                        <Controller
                          name="metaTitle"
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              variant="outline"
                              inputSize="sm"
                              disabled={fieldDisabled}
                              className={GO_LIVE_INPUT_CLASS}
                              placeholder="Smith Roofing — Trusted local experts"
                            />
                          )}
                        />
                      </Field>

                      <Field label="Description" variant="goLive">
                        <Controller
                          name="metaDescription"
                          control={control}
                          render={({ field }) => (
                            <Textarea
                              {...field}
                              disabled={fieldDisabled}
                              className={GO_LIVE_TEXTAREA_CLASS}
                              rows={3}
                              placeholder="Brief summary…"
                            />
                          )}
                        />
                      </Field>

                      <Field label="Browser tab icon" variant="goLive">
                        {activeSite && (
                          <FaviconUploader
                            site={activeSite}
                            boardSlug={boardSlug}
                            disabled={fieldDisabled}
                            onUpdated={handleSiteMediaUpdated}
                          />
                        )}
                      </Field>

                      <Controller
                        name="robotsIndex"
                        control={control}
                        render={({ field }) => (
                          <GoLiveToggleRow
                            id="site-robotsIndex"
                            label="Allow indexing"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={fieldDisabled}
                          />
                        )}
                      />
                    </GoLiveFormSection>

                    <GoLiveFormSection
                      title="Social sharing"
                      subtitle="How your site looks when someone pastes the link into social apps."
                      helpText="Upload a share image (recommended 1200×630 px). Share title and description override search appearance in link previews when filled in — leave blank to use those defaults."
                    >
                      <Field label="Share image" variant="goLive">
                        {activeSite && (
                          <OgImageUploader
                            site={activeSite}
                            boardSlug={boardSlug}
                            disabled={fieldDisabled}
                            onUpdated={handleSiteMediaUpdated}
                          />
                        )}
                      </Field>

                      <Field label="Share title" variant="goLive">
                        <Controller
                          name="ogTitle"
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              variant="outline"
                              inputSize="sm"
                              disabled={fieldDisabled}
                              className={GO_LIVE_INPUT_CLASS}
                              placeholder="Optional — uses page title when empty"
                            />
                          )}
                        />
                      </Field>

                      <Field label="Share description" variant="goLive">
                        <Controller
                          name="ogDescription"
                          control={control}
                          render={({ field }) => (
                            <Textarea
                              {...field}
                              disabled={fieldDisabled}
                              className={GO_LIVE_TEXTAREA_CLASS}
                              rows={3}
                              placeholder="Optional — uses description when empty"
                            />
                          )}
                        />
                      </Field>
                    </GoLiveFormSection>

                    <Collapsible open={mobilePreviewOpen} onOpenChange={setMobilePreviewOpen} className="lg:hidden">
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          aria-expanded={mobilePreviewOpen}
                          className="flex w-full items-center justify-between border-t border-border pt-6 text-sm font-medium text-muted-foreground"
                        >
                          Live preview
                          <ChevronDown className={cn("size-4 transition-transform", mobilePreviewOpen && "rotate-180")} />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-5">
                        <LivePreviewPanel {...previewProps} />
                      </CollapsibleContent>
                    </Collapsible>

                    <GoLiveFormSection
                      title="Crawler rules"
                      subtitle="Fine-grained control over how search engines crawl your site."
                      helpText="Follow links: when off, crawlers are asked not to follow links on this site. Twitter card type: usually leave as summary_large_image for large image previews on X."
                    >
                      <Controller
                        name="robotsFollow"
                        control={control}
                        render={({ field }) => (
                          <GoLiveToggleRow
                            id="site-robotsFollow"
                            label="Follow links"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={fieldDisabled}
                          />
                        )}
                      />

                      <Field label="Twitter card type" variant="goLive">
                        <Controller
                          name="twitterCard"
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              variant="outline"
                              inputSize="sm"
                              disabled={fieldDisabled}
                              className={cn(GO_LIVE_INPUT_CLASS, "font-mono")}
                            />
                          )}
                        />
                      </Field>
                    </GoLiveFormSection>
                  </div>
                </div>
              </div>

              <div className="hidden min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-l border-border bg-muted/30 lg:flex">
                <SiteEditorPreviewHeader />
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6 pt-4">
                  <LivePreviewPanel {...previewProps} fit />
                </div>
              </div>
            </div>

            <SiteEditorFooter subtitle={publishSubtitle} includedCount={includedPages.length} statusChipId={STATUS_CHIP_ID} />
          </div>
        </div>
        </div>
      )}
    </div>
  );
}
